import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Compass, Loader2, BookOpen } from "lucide-react";
import Reveal from "../components/Reveal";

/**
 * Adventures — landing page for story-shaped tours through the curriculum.
 * Renders every adventure returned by GET /api/adventures as a large
 * pop-up-book card with kicker + one-line + act count + CTA.
 */
export default function Adventures() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/adventures").then(({ data }) => setData(data)).catch(() => setData({ adventures: [] }));
  }, []);

  return (
    <div className="container-narrow py-14 md:py-20" data-testid="adventures-page">
      <Reveal className="mb-10">
        <div className="inline-flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.28em] font-bold text-marigold">
          <Compass className="w-3.5 h-3.5" /> Adventure Narratives
        </div>
        <h1 className="font-heading text-5xl md:text-6xl font-semibold text-ink leading-[1.05] mb-4 max-w-3xl">
          Learn by <span className="italic text-marigold gold-underline">going somewhere</span>.
        </h1>
        <p className="text-lg text-ink2 leading-relaxed max-w-2xl">
          Some ideas are easier to hold if you're on a story instead of a syllabus.  Each adventure
          threads real curriculum chapters through a single narrative — you learn while the plot
          moves.
        </p>
      </Reveal>

      {!data ? (
        <div className="py-20 text-center text-ink2"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : (
        <div className="grid gap-6" data-testid="adventures-list">
          {(data.adventures || []).map((a, i) => (
            <Reveal key={a.slug} delay={(i % 4) + 1}>
              <AdventureCard adv={a} />
            </Reveal>
          ))}
          {data.adventures?.length === 0 && (
            <div className="rounded-3xl border-2 border-dashed border-ink/30 p-10 text-center text-ink2">
              <p className="italic mb-2">Ms. Pradhan is writing the first one.</p>
              <p className="text-sm">Check back after the next chapter import.</p>
            </div>
          )}
        </div>
      )}

      <Reveal className="mt-14">
        <div className="rounded-3xl border-2 border-ink bg-cream shadow-[6px_6px_0_0_#1F1A17] p-7 flex items-center gap-5 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-marigold/20 text-marigold flex items-center justify-center">
            <BookOpen className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">Prefer the reference?</div>
            <h3 className="font-heading text-2xl font-semibold text-ink">Every idea also lives in the twelve worlds.</h3>
            <p className="text-sm text-ink2 mt-1">Adventures pull chapters from there; the twelve worlds hold every chapter, indexed and calm.</p>
          </div>
          <Link to="/learn" className="btn-outline-ink text-sm inline-flex items-center gap-1.5">
            The twelve worlds <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>
    </div>
  );
}

function AdventureCard({ adv }) {
  return (
    <Link
      to={`/adventures/${adv.slug}`}
      className="group block rounded-3xl border-2 border-ink bg-cream shadow-[8px_8px_0_0_#1F1A17] hover:-translate-y-1 hover:shadow-[12px_12px_0_0_#1F1A17] transition-all p-7 md:p-9 relative overflow-hidden"
      data-testid={`adventure-card-${adv.slug}`}
    >
      <div className="absolute top-4 right-6 font-heading text-8xl md:text-9xl font-semibold text-ink/[0.05] leading-none select-none pointer-events-none">
        {String((adv.kicker || "").split(" ").pop() || "I")}
      </div>

      <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-marigold mb-3 relative inline-flex items-center gap-2">
        {adv.kicker} · {adv.act_count} acts
        {adv.is_epilogue && (
          <span
            className="text-[9px] rounded-full border border-marigold text-marigold px-1.5 py-0.5 font-mono tracking-widest"
            data-testid={`adventure-epilogue-${adv.slug}`}
          >
            EPILOGUE
          </span>
        )}
      </div>
      <h3 className={`font-heading text-3xl md:text-4xl font-semibold ${adv.cover_accent || "text-ink"} leading-tight mb-4 relative max-w-3xl`}>
        {adv.title}
      </h3>
      <p className="text-base md:text-lg text-ink2 italic leading-relaxed max-w-2xl mb-6 relative">
        {adv.one_line}
      </p>
      <div className="inline-flex items-center gap-1.5 text-sm font-bold text-ink group-hover:text-marigold transition-colors relative">
        Begin the story <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}
